import React, { useState, useEffect, useCallback } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Box,
  Stack,
  Card,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import { rabbitmqTheme } from './theme.js';
import { Navbar } from './components/Navbar.jsx';
import { RabbitmqDashboard } from './components/RabbitmqDashboard.jsx';
import { QueueHeader } from './components/QueueHeader.jsx';
import { StatCards } from './components/StatCards.jsx';
import { QueuedMessagesChart } from './components/QueuedMessagesChart.jsx';
import { MessageRatesChart } from './components/MessageRatesChart.jsx';
import { QueueDetailsSection } from './components/QueueDetailsSection.jsx';
import { PeekMessagesModal } from './components/PeekMessagesModal.jsx';
import { ConsumersModal } from './components/ConsumersModal.jsx';
import { BindingsModal } from './components/BindingsModal.jsx';
import { PurgeModal } from './components/PurgeModal.jsx';
import { SettingsModal } from './components/SettingsModal.jsx';
import { TimeSeriesConfigModal } from './components/TimeSeriesConfigModal.jsx';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useRabbitmqLive } from './hooks/useRabbitmqLive.js';
import { rabbitmqApi } from '../../../services/rabbitmqServices/rabbitmqService';

const RANGE_API_PARAMS = {
  '1m': { age: 60, incr: 5 },
  '10m': { age: 600, incr: 5 },
  '1h': { age: 3600, incr: 30 },
  '8h': { age: 28800, incr: 240 },
  '1d': { age: 86400, incr: 720 },
};

export default function RabbitmqDashboardPage() {
  const { snapshot } = useRabbitmqLive();

  const [theme, setTheme] = useState('light');
  const [refreshInterval, setRefreshInterval] = useState(5);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState('');

  // Global & Per-Chart TimeSeries Configurations
  const [activeRange, setActiveRange] = useState('10m');
  const [queuedConfig, setQueuedConfig] = useState({
    display: 'chart',
    size: 'medium',
    range: '10m',
  });
  const [ratesConfig, setRatesConfig] = useState({
    display: 'chart',
    size: 'medium',
    range: '10m',
  });

  // Active modal for time-series popup
  const [activeConfigTarget, setActiveConfigTarget] = useState(null);

  // Selected Queue for drill-down inspection
  const [activeQueue, setActiveQueue] = useState('');
  const [showDrilldown, setShowDrilldown] = useState(true);

  // Drilldown data
  const [queueData, setQueueData] = useState({});
  const [availableQueues, setAvailableQueues] = useState([]);
  const [overview, setOverview] = useState({
    cluster_name: '',
    rabbitmq_version: '',
    erlang_version: '',
    is_simulated: false,
  });

  // Action Modals State
  const [isPeekOpen, setIsPeekOpen] = useState(false);
  const [isConsumersOpen, setIsConsumersOpen] = useState(false);
  const [isBindingsOpen, setIsBindingsOpen] = useState(false);
  const [isPurgeOpen, setIsPurgeOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  useEffect(() => {
    setTheme('light');
    try {
      document.documentElement.classList.remove('dark');
      localStorage.removeItem('rmq_theme');
    } catch (_) {}
  }, []);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
  };

  const [overviewTotals, setOverviewTotals] = useState(null);
  const [chartScope, setChartScope] = useState('totals');

  const fetchDrilldownData = useCallback(
    async (rangeToUse = activeRange, queueToUse = activeQueue) => {
      setIsRefreshing(true);
      try {
        const { age, incr } = RANGE_API_PARAMS[rangeToUse] || RANGE_API_PARAMS['10m'];
        const params = {
          lengths_age: age,
          lengths_incr: incr,
          msg_rates_age: age,
          msg_rates_incr: incr,
        };

        const [dData, list, oData] = await Promise.all([
          queueToUse
            ? rabbitmqApi.getQueueDetail(queueToUse, params).catch(() => null)
            : Promise.resolve(null),
          rabbitmqApi.getQueues().catch(() => []),
          rabbitmqApi.getOverview(params).catch(() => ({})),
        ]);

        if (dData) {
          setQueueData(dData);
          setError('');
        }

        if (Array.isArray(list)) {
          setAvailableQueues(list);
        }

        if (oData) {
          setOverview({
            cluster_name: oData.cluster_name,
            rabbitmq_version: oData.rabbitmq_version,
            erlang_version: oData.erlang_version,
            is_simulated: Boolean(oData.is_simulated),
          });
          setOverviewTotals(oData);
        }
      } catch (err) {
        console.error('Failed to fetch queue drilldown:', err);
      } finally {
        setIsRefreshing(false);
      }
    },
    [activeRange, activeQueue],
  );

  useEffect(() => {
    if (snapshot?.queues && snapshot.queues.length > 0 && !activeQueue) {
      setActiveQueue(snapshot.queues[0].name);
    }
  }, [snapshot, activeQueue]);

  useEffect(() => {
    fetchDrilldownData(activeRange, activeQueue);

    if (refreshInterval <= 0) return;
    const timer = setInterval(() => {
      fetchDrilldownData(activeRange, activeQueue);
    }, refreshInterval * 1000);

    return () => clearInterval(timer);
  }, [fetchDrilldownData, activeRange, activeQueue, refreshInterval]);

  const handleQueueSelected = (queueName) => {
    setActiveQueue(queueName);
    setChartScope('queue');
    setShowDrilldown(true);
    fetchDrilldownData(activeRange, queueName);
    setTimeout(() => {
      document.getElementById('queue-drilldown-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleConfigChange = (newConfig) => {
    if (newConfig.range && newConfig.range !== activeRange) {
      setActiveRange(newConfig.range);
      setQueuedConfig((prev) => ({ ...prev, range: newConfig.range }));
      setRatesConfig((prev) => ({ ...prev, range: newConfig.range }));
      fetchDrilldownData(newConfig.range, activeQueue);
    }

    if (activeConfigTarget === 'queued') {
      setQueuedConfig((prev) => ({ ...prev, ...newConfig }));
    } else if (activeConfigTarget === 'rates') {
      setRatesConfig((prev) => ({ ...prev, ...newConfig }));
    }
  };

  const isTotals = chartScope === 'totals';

  const readySamples = isTotals
    ? overviewTotals?.queue_totals?.messages_ready_details?.samples || []
    : queueData.messages_ready_details?.samples || [];

  const unackedSamples = isTotals
    ? overviewTotals?.queue_totals?.messages_unacknowledged_details?.samples || []
    : queueData.messages_unacknowledged_details?.samples || [];

  const totalSamples = isTotals
    ? overviewTotals?.queue_totals?.messages_details?.samples || []
    : queueData.messages_details?.samples || [];

  const currentReady = isTotals
    ? overviewTotals?.queue_totals?.messages_ready ?? snapshot?.overview?.messagesReady ?? 0
    : queueData.messages_ready ?? 0;

  const currentUnacked = isTotals
    ? overviewTotals?.queue_totals?.messages_unacknowledged ?? snapshot?.overview?.messagesUnacknowledged ?? 0
    : queueData.messages_unacknowledged ?? 0;

  const currentTotal = isTotals
    ? overviewTotals?.queue_totals?.messages ?? snapshot?.overview?.messagesTotal ?? 0
    : queueData.messages ?? 0;

  const statsSource = isTotals
    ? overviewTotals?.message_stats || {}
    : queueData.message_stats || {};

  const publishRate = statsSource.publish_details?.rate ?? 0.0;
  const deliverManualRate = statsSource.deliver_details?.rate ?? 0.0;
  const deliverAutoRate = statsSource.deliver_no_ack_details?.rate ?? 0.0;
  const consumerAckRate = statsSource.ack_details?.rate ?? 0.0;
  const redeliveredRate = statsSource.redeliver_details?.rate ?? 0.0;
  const getManualRate = statsSource.get_details?.rate ?? 0.0;
  const getAutoRate = statsSource.get_no_ack_details?.rate ?? 0.0;
  const getEmptyRate = statsSource.get_empty_details?.rate ?? 0.0;

  const publishSamples = statsSource.publish_details?.samples || [];
  const deliverManualSamples = statsSource.deliver_details?.samples || [];
  const ackSamples = statsSource.ack_details?.samples || [];
  const redeliverSamples = statsSource.redeliver_details?.samples || [];


  const currentUsername = snapshot?.user || '';
  const currentVhost = decodeURIComponent(snapshot?.vhost || queueData.vhost || '/');
  const currentOverview = {
    cluster_name: snapshot?.overview?.clusterName || overview.cluster_name || '',
    rabbitmq_version: snapshot?.overview?.rabbitmqVersion || overview.rabbitmq_version || '',
    erlang_version: overview.erlang_version || '',
  };

  return (
    <ThemeProvider theme={rabbitmqTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', pb: 10, bgcolor: 'background.default' }}>
        {/* Top Navigation */}
        <Navbar
          overview={currentOverview}
          username={currentUsername}
          vhost={currentVhost}
          refreshInterval={refreshInterval}
          onRefreshChange={setRefreshInterval}
          isRefreshing={isRefreshing}
          onManualRefresh={() => fetchDrilldownData(activeRange, activeQueue)}
          theme={theme}
          onToggleTheme={toggleTheme}
          onOpenSettings={() => setIsSettingsOpen(true)}
        />

        {/* Main Container */}
        <Container maxWidth={false} sx={{ pt: 2.5, px: { xs: 2, sm: 3, md: 4 } }}>
          <Stack spacing={4}>
            {/* Error Notification */}
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* 1. Live RabbitMQ Monitoring Dashboard */}
            <Box component="section">
              <RabbitmqDashboard
                selectedQueueName={activeQueue}
                onSelectQueueForInspection={handleQueueSelected}
              />
            </Box>

            {/* 2. Deep Drill-Down & Inspection Section */}
            <Card
              id="queue-drilldown-section"
              component="section"
              sx={{ p: 3 }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: '#006951',
                      display: 'block',
                    }}
                  >
                    Queue Diagnostics & Drill-Down
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} mt={0.2}>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                      Inspecting:
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        color: '#006951',
                      }}
                    >
                      {chartScope === 'totals' ? 'All Queues (Broker Totals)' : activeQueue || 'No Queue Selected'}
                    </Typography>
                  </Stack>
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowDrilldown(!showDrilldown)}
                  startIcon={showDrilldown ? <ChevronUp style={{ width: 14, height: 14 }} /> : <ChevronDown style={{ width: 14, height: 14 }} />}
                  sx={{ borderColor: 'divider', color: 'text.secondary', fontSize: '0.75rem' }}
                >
                  {showDrilldown ? 'Collapse Details' : 'Expand Details'}
                </Button>
              </Stack>

              {showDrilldown && (
                <Stack spacing={3.5} sx={{ pt: 2.5 }}>
                  {/* Queue Header & Quick Actions */}
                  <QueueHeader
                    queueName={queueData.name || activeQueue}
                    availableQueues={availableQueues}
                    onSelectQueue={handleQueueSelected}
                    vhost={queueData.vhost || '/'}
                    state={queueData.state || 'running'}
                    consumerCount={queueData.consumers ?? 0}
                    bindingCount={queueData.bindings_count ?? 0}
                    onPeekMessages={() => setIsPeekOpen(true)}
                    onViewConsumers={() => setIsConsumersOpen(true)}
                    onViewBindings={() => setIsBindingsOpen(true)}
                    onPurgeQueue={() => setIsPurgeOpen(true)}
                  />

                  {/* Scope Switcher: All Queues (Totals) vs Selected Queue */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        size="small"
                        variant={chartScope === 'totals' ? 'contained' : 'outlined'}
                        onClick={() => setChartScope('totals')}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          bgcolor: chartScope === 'totals' ? '#006951' : 'transparent',
                          color: chartScope === 'totals' ? '#fff' : 'text.primary',
                          borderColor: '#006951',
                          '&:hover': {
                            bgcolor: chartScope === 'totals' ? '#00523f' : 'action.selected',
                          },
                        }}
                      >
                        All Queues (Totals)
                      </Button>
                      <Button
                        size="small"
                        variant={chartScope === 'queue' ? 'contained' : 'outlined'}
                        onClick={() => setChartScope('queue')}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          bgcolor: chartScope === 'queue' ? '#006951' : 'transparent',
                          color: chartScope === 'queue' ? '#fff' : 'text.primary',
                          borderColor: '#006951',
                          '&:hover': {
                            bgcolor: chartScope === 'queue' ? '#00523f' : 'action.selected',
                          },
                        }}
                      >
                        Selected Queue: {queueData.name || activeQueue || 'None'}
                      </Button>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {chartScope === 'totals'
                        ? 'Displaying broker-wide Totals (matches RabbitMQ Totals across all queues)'
                        : `Displaying metrics for queue "${queueData.name || activeQueue}"`}
                    </Typography>
                  </Box>

                  {/* Stat Cards for Selected Queue or Broker Totals */}
                  <StatCards
                    total={currentTotal}
                    ready={currentReady}
                    unacked={currentUnacked}
                    ackRate={consumerAckRate}
                    deliverRate={deliverManualRate}
                  />

                  {/* Primary Chart: Queued messages (Ready, Unacked, Total) */}
                  <QueuedMessagesChart
                    readySamples={readySamples}
                    unackedSamples={unackedSamples}
                    totalSamples={totalSamples}
                    currentReady={currentReady}
                    currentUnacked={currentUnacked}
                    currentTotal={currentTotal}
                    theme={theme}
                    config={queuedConfig}
                    onOpenConfig={() => setActiveConfigTarget('queued')}
                  />

                  {/* Secondary Chart: Message rates */}
                  <MessageRatesChart
                    stats={{
                      publishRate,
                      deliverManualRate,
                      deliverAutoRate,
                      consumerAckRate,
                      redeliveredRate,
                      getManualRate,
                      getAutoRate,
                      getEmptyRate,
                      publishSamples,
                      deliverManualSamples,
                      ackSamples,
                      redeliverSamples,
                    }}
                    theme={theme}
                    config={ratesConfig}
                    onOpenConfig={() => setActiveConfigTarget('rates')}
                  />

                  {/* Queue Details: Features, Arguments, DLX, Consumers */}
                  <QueueDetailsSection
                    features={queueData.arguments || {}}
                    state={queueData.state || 'running'}
                    consumerCount={queueData.consumers ?? 0}
                    bindingCount={queueData.bindings_count ?? 0}
                  />
                </Stack>
              )}
            </Card>
          </Stack>
        </Container>

        {/* Time Series Configuration Modal */}
        <TimeSeriesConfigModal
          isOpen={activeConfigTarget !== null}
          onClose={() => setActiveConfigTarget(null)}
          config={
            activeConfigTarget === 'queued'
              ? queuedConfig
              : activeConfigTarget === 'rates'
              ? ratesConfig
              : queuedConfig
          }
          onChange={handleConfigChange}
          title={
            activeConfigTarget === 'queued'
              ? 'Queued Messages Time Series'
              : 'Message Rates Time Series'
          }
        />

        {/* Action Modals */}
        <PeekMessagesModal
          isOpen={isPeekOpen}
          onClose={() => setIsPeekOpen(false)}
          queueName={queueData.name || activeQueue}
          vhost={queueData.vhost || '/'}
        />

        <ConsumersModal
          isOpen={isConsumersOpen}
          onClose={() => setIsConsumersOpen(false)}
          queueName={queueData.name || activeQueue}
          consumers={queueData.consumer_details || []}
        />

        <BindingsModal
          isOpen={isBindingsOpen}
          onClose={() => setIsBindingsOpen(false)}
          queueName={queueData.name || activeQueue}
          vhost={queueData.vhost || '/'}
        />

        <PurgeModal
          isOpen={isPurgeOpen}
          onClose={() => setIsPurgeOpen(false)}
          queueName={queueData.name || activeQueue}
          vhost={queueData.vhost || '/'}
          onPurged={() => fetchDrilldownData(activeRange, activeQueue)}
        />

        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          onSettingsSaved={(savedData) => {
            if (savedData?.useSimulation !== undefined) {
              setOverview((prev) => ({ ...prev, is_simulated: Boolean(savedData.useSimulation) }));
            }
            fetchDrilldownData(activeRange, activeQueue);
          }}
        />
      </Box>
    </ThemeProvider>
  );
}
